/**
 * The plan, on screen.
 *
 * SECTION FOR SECTION WITH THE DOWNLOADABLE REPORT. This walks the same
 * `buildSkeleton` spine the PDF walks and hands each section to a renderer, the
 * way `pdfReport` hands each to a draw function. Same sections, same numbers,
 * same order, same figures — the content itself comes from
 * `Helpers/portfolioPlanner/reportContent`, which both surfaces read.
 *
 * The plan lives in memory only: the API has no persistence yet, which is why
 * the report nudges toward downloading before leaving.
 *
 * Ported from the user platform's PortfolioPlanner/PlannerReport.tsx.
 */

import React, { useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { buildPlanAnalytics } from "../../../Helpers/portfolioPlanner/analytics";
import {
  planReference,
  reportDate,
} from "../../../Helpers/portfolioPlanner/format";
import {
  applyCalloutBudget,
  buildSkeleton,
} from "../../../Helpers/portfolioPlanner/report/blocks";
import { derivePlanFacts } from "../../../Helpers/portfolioPlanner/report/derive";
import { buildBlocks } from "../../../Helpers/portfolioPlanner/report/rules";
import {
  coverFacts,
  coverStandfirst,
} from "../../../Helpers/portfolioPlanner/reportContent";
import type { ReportContext } from "../../../Helpers/portfolioPlanner/reportContent";
import { buildRetirementAdequacy } from "../../../Helpers/portfolioPlanner/retirement";
import type {
  PlanResult,
  PlannerConfig,
} from "../../../Helpers/portfolioPlanner/types";
import { InlineLoader } from "./PlannerFields";
import ReportSection from "./ReportSections";
import { Callout } from "./ReportBits";
import styles from "./portfolio-planner.module.css";

interface PlannerReportProps {
  plan: PlanResult;
  config?: PlannerConfig;
  /** Shown on the cover line so it is obvious whose plan this is. */
  investorName?: string;
  onBack: () => void;
  /** Absent when the plan implies no orders. */
  onPlaceOrders?: () => void;
}

const PlannerReport = ({
  plan,
  config,
  investorName,
  onBack,
  onPlaceOrders,
}: PlannerReportProps) => {
  const [status, setStatus] = useState<"idle" | "building" | "error">("idle");
  const [downloadError, setDownloadError] = useState("");

  /* Assembled exactly as the PDF assembles it — same facts, same blocks, same
     budget, same skeleton — so the two cannot disagree about what exists. */
  const { ctx, blocks, demoted, sections } = useMemo(() => {
    const facts = derivePlanFacts(plan, {
      inflationDefault: config ? config.inflationDefault : undefined,
      tdsPct: plan.assumptions ? plan.assumptions.tdsOnBondCouponPct : undefined,
    });
    const budgeted = applyCalloutBudget(buildBlocks(facts));
    const skeleton = buildSkeleton(facts, {
      overflow: budgeted.demoted.length > 0,
    });

    const retirementGoal =
      plan.goals.find((goal) => goal.type === "Retirement") ||
      plan.goals.find((goal) => goal.isLongest) ||
      plan.goals[0];
    const adequacy =
      plan.swp && retirementGoal
        ? buildRetirementAdequacy(plan.swp, {
            startAge:
              plan.client.ageY +
              plan.client.ageM / 12 +
              retirementGoal.totalMonths / 12,
            yearsToStart: retirementGoal.totalMonths / 12,
            inflationForToday: retirementGoal.infl,
          })
        : null;

    const context: ReportContext = {
      plan,
      facts,
      analytics: buildPlanAnalytics(plan),
      adequacy,
    };

    return {
      ctx: context,
      blocks: budgeted.blocks,
      demoted: budgeted.demoted,
      sections: skeleton,
    };
  }, [plan, config]);

  const reference = planReference(plan.generatedAt, plan.client.name);

  // jsPDF plus four embedded font weights — loaded only when a report is
  // actually asked for, so the planner's first paint never carries them.
  const download = async () => {
    setStatus("building");
    setDownloadError("");
    try {
      const { downloadPlanPdf } = await import(
        "../../../Helpers/portfolioPlanner/pdfReport"
      );
      downloadPlanPdf(plan, { config, reference });
      setStatus("idle");
    } catch (error) {
      setStatus("error");
      setDownloadError(
        (error as Error)?.message ||
          "The report could not be built. Please try again."
      );
    }
  };

  return (
    <div className={styles.report}>
      {/* The cover, as a screen can carry one: who it is for and the profile
          the plan was built against. */}
      <header className={styles.head}>
        <div className={styles.headCopy}>
          <span className={styles.kicker}>Goal plan</span>
          <h3>{plan.client.name || investorName || "This plan"}</h3>
          <p className={styles.standfirst}>{coverStandfirst(ctx.facts)}</p>
          <div className={styles.meta}>
            <span>{reference}</span>
            <span>Generated {reportDate(plan.generatedAt)}</span>
            {investorName ? <span>Prepared by an admin for {investorName}</span> : null}
          </div>
        </div>
        <div className={styles.headActions}>
          <button type="button" className={styles.ghost} onClick={onBack}>
            <ArrowBackIcon style={{ fontSize: 17 }} aria-hidden="true" />
            Change the inputs
          </button>
          {onPlaceOrders && plan.orders && plan.orders.length ? (
            <button type="button" className={styles.tonal} onClick={onPlaceOrders}>
              <ReceiptLongOutlinedIcon style={{ fontSize: 17 }} aria-hidden="true" />
              Place these orders
            </button>
          ) : null}
          <button
            type="button"
            className={styles.primary}
            onClick={download}
            disabled={status === "building"}
            aria-busy={status === "building" || undefined}
          >
            {status === "building" ? (
              <>
                <InlineLoader />
                Building the report…
              </>
            ) : (
              <>
                <DownloadIcon style={{ fontSize: 17 }} aria-hidden="true" />
                Download the report
              </>
            )}
          </button>
        </div>
      </header>

      <div className={styles.coverFacts}>
        {coverFacts(plan).map((fact) => (
          <div key={fact.label}>
            <span>{fact.label}</span>
            <b>{fact.value}</b>
          </div>
        ))}
      </div>

      {status === "error" ? (
        <Callout tone="warn">
          {downloadError || "The report could not be built. Please try again."}
        </Callout>
      ) : null}

      {/* Every section the document has, in the document's order. */}
      {sections.map((section) => (
        <ReportSection
          key={`${section.key}-${section.subjectId == null ? "plan" : section.subjectId}-${section.number}`}
          ctx={ctx}
          section={section}
          blocks={blocks}
          demoted={demoted}
          sections={sections}
          config={config}
        />
      ))}

      <p className={styles.footNote}>
        <InfoOutlinedIcon style={{ fontSize: 17, flex: "0 0 auto" }} aria-hidden="true" />
        <span>
          Plans are not stored yet — there is no endpoint to fetch this one back.
          Download the report before closing this tab, or it has to be generated
          again.
        </span>
      </p>

      <p className={styles.disclaimer}>
        Educational planning tool, not investment advice. Mutual funds carry
        market risk; every return shown is an assumption, never a guarantee. Bond
        prices are indicative at par where no live price is available.
      </p>
    </div>
  );
};

export default PlannerReport;
